import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Stack, Box, Typography, Switch, FormControlLabel,
} from "@mui/material";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";

import api from "../api";
import LabelChips from "./LabelChips";

const DEFAULT_CODE = `"""Custom classifier — runs in the shared classify venv.

    load(config) -> ctx
    classify(ctx, sequence, labels, config) -> {"labels": [...], "scores": [...]}
"""


def load(config):
    return None


def classify(ctx, sequence, labels, config):
    return {"labels": list(labels), "scores": [0.0 for _ in labels]}
`;

// Create OR edit a classifier. `classifier=null` → create; otherwise edit (name +
// kind are fixed). Zero-shot = HF model id + labels; custom = an authored module.
export default function ClassifierModal({ classifier, onClose }) {
  const editing = !!classifier;
  const [f, setF] = useState({
    name: classifier?.name || "",
    kind: classifier?.kind || "zeroshot",
    description: classifier?.description || "",
    model_id: classifier?.model_id || "",
    default_labels: classifier?.default_labels || [],
    multi_label: classifier?.multi_label ?? true,
    device: classifier?.device || "cpu",
    eligible_gpus: (classifier?.eligible_gpus || []).join(", "),
    vram_estimate_mb: classifier?.vram_estimate_mb || "",
    code: DEFAULT_CODE,
  });
  const [busy, setBusy] = useState(false);
  const codeTouched = useRef(false);
  const codeLoaded = useRef(false);
  const up = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const parseGpus = (s) => (s || "").split(/[,\s]+/).map((x) => parseInt(x, 10)).filter((n) => !Number.isNaN(n));
  const deviceBody = () => (f.device === "gpu"
    ? { device: "gpu", eligible_gpus: parseGpus(f.eligible_gpus),
        vram_estimate_mb: f.vram_estimate_mb ? parseInt(f.vram_estimate_mb, 10) : null }
    : { device: "cpu" });

  // Editing a custom classifier: load its current code — but don't clobber edits
  // the user may have started before the fetch resolved.
  useEffect(() => {
    if (editing && classifier.kind === "custom") {
      api.get(`/api/classifiers/${classifier.name}/code`, { silent: true })
        .then((d) => { codeLoaded.current = true; if (!codeTouched.current) up("code", d.code || DEFAULT_CODE); })
        .catch(() => {});
    }
  }, [editing, classifier]);

  const save = async () => {
    setBusy(true);
    try {
      if (editing) {
        const body = {
          description: f.description, default_labels: f.default_labels,
          multi_label: f.multi_label, ...deviceBody(),
        };
        if (f.kind === "zeroshot") body.model_id = f.model_id;
        // Only write code back if it was actually loaded (or the user edited it) —
        // never overwrite the stored module with the stub when the fetch is still
        // pending or failed.
        if (f.kind === "custom" && (codeLoaded.current || codeTouched.current)) body.code = f.code;
        await api.patch(`/api/classifiers/${classifier.name}`, body);
        toast.success("Classifier saved");
      } else {
        await api.post("/api/classifiers", {
          name: f.name, kind: f.kind, description: f.description,
          model_id: f.kind === "zeroshot" ? f.model_id : null,
          default_labels: f.default_labels, multi_label: f.multi_label,
          code: f.kind === "custom" ? f.code : undefined,
          ...deviceBody(),
        });
        toast.success("Classifier created");
      }
      onClose(true);
    } catch {
      /* api wrapper toasts */
    } finally {
      setBusy(false);
    }
  };

  const canSave = editing || (f.name && (f.kind === "custom" || f.model_id));

  return (
    <Dialog open onClose={() => onClose()} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit ${classifier.name}` : "New classifier"}</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small" label="Name" placeholder="support-intent"
            value={f.name} onChange={(e) => up("name", e.target.value)}
            disabled={editing} sx={{ flex: 1 }}
          />
          <TextField
            size="small" label="Kind" select value={f.kind}
            onChange={(e) => up("kind", e.target.value)} disabled={editing} sx={{ width: 160 }}
          >
            <MenuItem value="zeroshot">zero-shot</MenuItem>
            <MenuItem value="custom">custom code</MenuItem>
          </TextField>
        </Stack>

        {f.kind === "zeroshot" && (
          <TextField
            fullWidth size="small" label="HuggingFace model id"
            placeholder="facebook/bart-large-mnli"
            value={f.model_id} onChange={(e) => up("model_id", e.target.value)}
            sx={{ mb: 1.5 }}
          />
        )}

        <TextField
          fullWidth size="small" label="Description" value={f.description}
          onChange={(e) => up("description", e.target.value)} sx={{ mb: 1.5 }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
          Default candidate labels (pre-fill the playground)
        </Typography>
        <Box sx={{ mb: 1 }}>
          <LabelChips labels={f.default_labels} onChange={(v) => up("default_labels", v)} />
        </Box>
        <FormControlLabel
          control={<Switch checked={f.multi_label} onChange={(e) => up("multi_label", e.target.checked)} />}
          label="Multi-label (independent scores per label)"
          sx={{ mb: 1 }}
        />

        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          <TextField
            size="small" label="Device" select value={f.device}
            onChange={(e) => up("device", e.target.value)} sx={{ width: 150 }}
            helperText={f.device === "gpu" ? "Scheduled GPU job (in the queue)" : "Shared CPU server"}
          >
            <MenuItem value="cpu">CPU</MenuItem>
            <MenuItem value="gpu">GPU</MenuItem>
          </TextField>
          {f.device === "gpu" && (
            <>
              <TextField
                size="small" label="Eligible GPUs" placeholder="any"
                value={f.eligible_gpus} onChange={(e) => up("eligible_gpus", e.target.value)}
                helperText="global indices, comma-sep; blank = any" sx={{ flex: 1 }}
              />
              <TextField
                size="small" label="VRAM est (MB)" type="number" placeholder="8192"
                value={f.vram_estimate_mb} onChange={(e) => up("vram_estimate_mb", e.target.value)}
                sx={{ width: 140 }}
              />
            </>
          )}
        </Stack>

        {f.kind === "custom" && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              Module — load(config) + classify(ctx, sequence, labels, config)
            </Typography>
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
              <CodeMirror value={f.code} height="240px" extensions={[python()]}
                onChange={(v) => { codeTouched.current = true; up("code", v); }} />
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} color="inherit">Cancel</Button>
        <Button onClick={save} disabled={busy || !canSave} variant="contained">
          {editing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
